<template>
    <div class="basic-table-wrapper" :style="{ 'max-height': props.maxHeight }">
        <div v-if="!(props.hideEditButton && props.hideDownloadButton)" class="header-row" :class="{ 'reduced-header': props.optionsTeleport && !hasSlotHeader && !props.title }">
            <h2 v-if="!hasSlotHeader && props.title">{{ props.title }}</h2>
            <slot name="header" />
            <div class="button-row" v-if="props.data.length">
                <conditional-teleport :to="props.optionsTeleport || null">
                    <action-button
                        v-if="props.plainEditButton && !props.hideEditButton"
                        class="icon-controls"
                        title="Edit table"
                        :icon="['fas', 'pencil']"
                        direction="left"
                        @click="openEditTableOverlay"
                    />
                    <prime-button v-else-if="!props.hideEditButton" v-tooltip.left="'Edit table'" aria-label="Edit table" text @click="openEditTableOverlay">
                        <i class="fa-solid fa-pencil" />
                    </prime-button>
                    <prime-button
                        v-if="!props.hideDownloadButton"
                        v-tooltip.left="'Download table'"
                        text
                        @click="openDownloadTableOverlay"
                    >
                        <i class="fa-solid fa-download" />
                    </prime-button>
                </conditional-teleport>
            </div>
        </div>
        <div
            v-if="props.data.length"
            class="table-wrapper"
            :class="{ 'hover-actions': hoverActions, 'mobile-actions-menu': showMobileActionsMenu }"
            @scroll="scrollTable"
        >
            <table>
                <thead>
                    <tr>
                        <th
                            v-if="props.actions?.length && hoverActions && showMobileActionsMenu"
                            class="hover-actions-spacer row-actions-leading-cell"
                        />
                        <th v-for="header in displayedHeaders" :key="header.value">
                            <span class="header-text" v-if="header.noFilter">
                                {{ getHeaderTitle(header) }}
                            </span>
                            <prime-button v-else class="header-button" text @click="openHeaderFilter($event, header.value)">
                                {{ getHeaderTitle(header) }}
                                <i v-if="sortHeader === header.value" :class="`fa-solid ${sortDirectionAscending ? 'fa-arrow-up' : 'fa-arrow-down'}`" />
                                <i v-if="!currentFilters[header.value].noFilter" class="fa-solid fa-filter" />
                            </prime-button>
                            <info-icon-with-tooltip
                                v-if="header.infoTooltip"
                                :tooltip-text="header.infoTooltip"
                            />
                        </th>
                        <th v-if="props.actions?.length && !hoverActions">
                            <span class="header-text">Actions</span>
                        </th>
                        <th
                            v-if="props.actions?.length && hoverActions && !showMobileActionsMenu"
                            class="hover-actions-spacer"
                        />
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(row, rowIndex) in visibleTableRows"
                        :key="rowIndex"
                        :class="{ 'cell-selection': props.cellSelectionHeaders, 'has-actions': props.actions?.length && hoverActions }"
                        @click="selectRow(row)">
                        <td
                            v-if="props.actions?.length && showMobileActionsMenu"
                            class="row-actions-overlay-cell row-actions-leading-cell"
                            @click.stop
                        >
                            <action-button
                                title="Row actions"
                                tooltip-left
                                class="row-actions-trigger"
                                @click.stop="openRowActionsMenu($event, row)"
                            >
                                <i class="fa-solid fa-ellipsis-vertical" />
                            </action-button>
                        </td>
                        <td
                            v-for="header in displayedHeaders"
                            :key="`row-${rowIndex}-${header.value}`"
                            :class="{ clickable: props.cellSelectionHeaders && props.cellSelectionHeaders.includes(header.value) }"
                            @click="selectCell(row[header.value], header.value)"
                            @keyup.enter="selectCell(row[header.value], header.value)"
                        >
                            <div v-html="formatCell(row[header.value], header)" />
                        </td>
                        <td
                            v-if="props.actions?.length && !showMobileActionsMenu"
                            :class="{ 'row-actions-overlay-cell': hoverActions, 'row-actions-static-cell': !hoverActions }"
                            @click.stop
                        >
                            <div class="row-actions-overlay">
                                <action-button
                                    v-for="(act, index) in enabledActions"
                                    :key="act.id"
                                    :style="{
                                        '--action-delay': index * 50,
                                        '--action-delay-leave': (enabledActions.length - 1 - index) * 50,
                                    }"
                                    :title="act.label"
                                    direction="left"
                                    class="action-button"
                                    :class="{ delete: act.id === 'delete' }"
                                    :is-disabled="isActionDisabled(row, act)"
                                    :disabled-reason="getDisabledReason(row, act)"
                                    @click.stop="emitAction(act.id, row)"
                                >
                                    <i :class="`fa-solid fa-${act.icon}`" />
                                </action-button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div v-else class="medium-padding">
            No data found
        </div>
        <popover class="arrow-colour-0" ref="headerFilter">
            <div v-if="selectedHeaderToFilter" class="header-filter">
                <div class="header-filter-section">
                    <h4>Filter</h4>
                    <template v-if="currentFilters[selectedHeaderToFilter].type === 'numeric'">
                        <min-max-selector
                            :model-value="
                                {
                                    min: (currentFilters[selectedHeaderToFilter].options as INumberValuesToInclude).min,
                                    max: (currentFilters[selectedHeaderToFilter].options as INumberValuesToInclude).max,
                                }"
                            @update:model-value="updateNumericRange(selectedHeaderToFilter, $event)" />
                        <div class="checkbox-wrapper">
                            <label for="absoluteValues">Use absolute values</label>
                            <select-button
                                :model-value="(currentFilters[selectedHeaderToFilter].options as INumberValuesToInclude).absolute"
                                :allow-empty="false"
                                :options="YES_NO_OPTIONS"
                                id="absoluteValues"
                                option-label="title"
                                option-value="value"
                                @update:model-value="updateUseAbsoluteValues(selectedHeaderToFilter, $event)"
                            />
                            <info-icon-with-tooltip
                                tooltip-text="Allows the above filter to be applied to both positive and negative values (e.g. a range of 10 to 100 with absolute values on will allow values of 10-100 and -10 to -100)"
                                direction="left"
                            />
                        </div>
                    </template>
                    <template v-else-if="currentFilters[selectedHeaderToFilter].type === 'text'">
                        <label for="excludeSelection">Selection</label>
                        <select-button
                            :model-value="(currentFilters[selectedHeaderToFilter].options as ITextValuesToInclude).exclude"
                            :options="EXCLUDE_SELECTION_OPTIONS"
                            option-label="title"
                            option-value="value"
                            id="excludeSelection"
                            :allow-empty="false"
                            @update:model-value="updateExcludeSelection(selectedHeaderToFilter, $event)"
                        />
                        <label for="selectedValues">Selected values</label>
                        <spark-multi-select
                            :model-value="(currentFilters[selectedHeaderToFilter].options as ITextValuesToInclude).values"
                            id="selectedValues"
                            placeholder="Select value to filter by"
                            :options="filterOptions"
                            @update:model-value="updateFilterValues(selectedHeaderToFilter, $event)"
                        />
                    </template>
                    <advanced-date-picker
                        v-else-if="currentFilters[selectedHeaderToFilter].type === 'date'"
                        :key="selectedHeaderToFilter"
                        clearable
                        :model-value="(currentFilters[selectedHeaderToFilter].options as IDateValuesToInclude).date"
                        @submit="updateDateFilter(selectedHeaderToFilter, $event)"
                        @reset:filters="resetDateFilter(selectedHeaderToFilter)"
                    />
                </div>
                <div class="header-filter-section">
                    <h4>Sort</h4>
                    <select-button
                        :model-value="selectedHeaderToFilter === sortHeader ? sortDirectionAscending : null"
                        option-label="title"
                        option-value="value"
                        :options="SORT_DIRECTION_OPTIONS"
                        @update:model-value="updateSortDirection(selectedHeaderToFilter, $event)"
                    />
                </div>
            </div>
        </popover>
        <popover ref="rowActionsPopover" class="arrow-colour-0" dismissable>
            <div v-if="activeActionRow" class="row-actions-menu">
                <action-button
                    v-for="act in enabledActions"
                    :key="act.id"
                    :title="act.label"
                    show-title-on-mobile-view
                    class="action-button"
                    :class="{ delete: act.id === 'delete' }"
                    :is-disabled="isActionDisabled(activeActionRow, act)"
                    :disabled-reason="getDisabledReason(activeActionRow, act)"
                    @click.stop="handleMobileRowAction(act.id)"
                >
                    <i :class="`fa-solid fa-${act.icon}`" />
                </action-button>
            </div>
        </popover>
        <popover class="arrow-colour-0" ref="editTableOverlay">
            <div class="edit-table-wrapper">
                <div class="edit-table-header">
                    <h4>Edit/sort columns</h4>
                    <info-icon-with-tooltip
                        tooltip-text="Use arrow buttons to reorder columns, click checkboxes to toggle show/hide"
                        direction="left"
                    />
                </div>
                <order-list
                    :model-value="tableHeaders"
                    @reorder="updateColumnOrder"
                >
                    <template #option="{ option, index }">
                        <div class="list-item" :key="`column-${index}`">
                            {{ option.value }}
                            <prime-checkbox
                                :model-value="!columnsToHide.includes(option.value)"
                                binary
                                @update:model-value="toggleColumn(option.value)"
                            />
                        </div>
                    </template>
                    <template #movetopicon>
                        <font-awesome-icon :icon="['fas', 'chevron-double-up']" />
                    </template>
                    <template #moveupicon>
                        <font-awesome-icon :icon="['fas', 'chevron-up']" />
                    </template>
                    <template #movedownicon>
                        <font-awesome-icon :icon="['fas', 'chevron-down']" />
                    </template>
                    <template #movebottomicon>
                        <font-awesome-icon :icon="['fas', 'chevron-double-down']" />
                    </template>
                </order-list>
                <div v-if="tableHasUnits" class="edit-table-units">
                    <label>Table units</label>
                    <select-button
                        v-model="tableUnitsSetting"
                        :options="TABLE_UNITS_OPTIONS"
                        option-label="name"
                        option-value="id"
                        :allow-empty="false"
                    >
                        <template #option="slotProps">
                            {{ slotProps.option.name }}
                            <info-icon-with-tooltip
                                v-if="slotProps.option.info"
                                :tooltip-text="slotProps.option.info"
                            />
                        </template>
                    </select-button>
                </div>
                <div class="edit-table-resets">
                    <label>Table Reset Options</label>
                    <div class="button-holder">
                        <prime-button
                            class="plain-btn"
                            v-tooltip.top="'Remove all filters from each header in the table'"
                            @click="confirmResetHeaders">
                            Reset headers
                        </prime-button>
                        <prime-button
                            class="plain-btn"
                            v-tooltip.top="'Reset order and hidden headers'"
                            @click="confirmResetTable">
                            Reset table
                        </prime-button>
                    </div>
                </div>
            </div>
        </popover>
        <popover ref="downloadTableOverlay">
            <table-download-type-select :file-name="props.downloadFileName || props.title || 'Basic Table'" :format-function="() => formatDataToJson()" />
        </popover>
    </div>
</template>
<script lang="ts" setup>
import MinMaxSelector from '@/components/powerMarkets/input/MinMax.vue';
import ConditionalTeleport from '@/components/layout/ConditionalTeleport.vue';
import PrimeCheckbox from 'primevue/checkbox';
import OrderList, { OrderListReorderEvent } from 'primevue/orderlist';
import TableDownloadTypeSelect from '@/components/powerMarkets/overlays/TableDownloadTypeSelect.vue';
import PrimeButton from 'primevue/button';
import SparkMultiSelect from '@/components/common/SparkMultiSelect.vue';
import Popover from 'primevue/popover';
import {
    computed, ref, Slots, useSlots, watch,
} from 'vue';
import InfoIconWithTooltip from '@/components/common/InfoIconWithTooltip.vue';
import { IFlagOption } from '@/interfaces/general/IFlagOption.ts';
import {
    IDateValuesToInclude, IHeadingFilters, INumberValuesToInclude, ITextValuesToInclude,
} from '@/interfaces/powerMarkets/table/IHeadingFilters.ts';
import { IMinMax } from '@/interfaces/general/IMinMax.ts';
import SelectButton from 'primevue/selectbutton';
import dayjs from 'dayjs';
import { IBasicTableHeader } from '@/interfaces/general/basicTable/IBasicTableHeader.ts';
import {
    IActionDescriptor, ITrend, TBasicTableActionId, TBasicTableCellType, TBasicTableSortValue, TIconCell,
} from '@/types/table/TBasicTableCellType.ts';
import { IIdName } from '@/interfaces/general/IIdName.ts';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { useConfirm } from 'primevue';
import AdvancedDatePicker from '@/components/powerMarkets/date-picker/AdvancedDatePicker.vue';
import { IAdvancedDatePickerModel } from '@/interfaces/general/IAdvancedDatePickerModel.ts';
import { getDateObjectFromDatePicker, getUnixBoundsFromTableDateFilterRange } from '@/utils/dateUtils.ts';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import ActionButton from '@/components/powerMarkets/common/ActionButton.vue';
import { escapeHtml } from '@/utils/htmlUtils.ts';
import { useUIStore } from '@/stores/uiStore.ts';
import { storeToRefs } from 'pinia';
import { getPlainTextFromHtmlString } from '@/utils/stringUtils.ts';

dayjs.extend(customParseFormat);
const confirm = useConfirm();

const ACTION_CONFIG: Array<IActionDescriptor> = [
    { id: 'view', label: 'View', icon: 'eye' },
    { id: 'run', label: 'Run', icon: 'play' },
    { id: 'info', label: 'Info', icon: 'circle-info' },
    { id: 'share', label: 'Share', icon: 'share' },
    { id: 'edit', label: 'Edit', icon: 'pencil' },
    { id: 'clone', label: 'Clone', icon: 'copy' },
    { id: 'delete', label: 'Delete', icon: 'trash' },
];

const props = defineProps<{
    data: Array<Record<string, TBasicTableCellType>>;
    headers: Array<IBasicTableHeader>;
    cellSelectionHeaders?: Array<string>;
    optionsTeleport?: HTMLElement | null;
    title?: string;
    downloadFileName?: string;
    filterOptionsUnsorted?: boolean;
    hideDownloadButton?: boolean;
    hideEditButton?: boolean;
    maxHeight?: string;
    externalFiltering?: boolean;
    sortDefaultAlphabetical?: boolean;
    actions?: Array<TBasicTableActionId>;
    hoverActions?: boolean;
    plainEditButton?: boolean;
    escapeTextCells?: boolean;
    initialSort?: {
        header: string;
        ascending: boolean;
    };
}>();

const emit = defineEmits<{
    'select:row': [Record<string, TBasicTableCellType>];
    'select:cell': [value: TBasicTableCellType, header: string];
    'update:filtered-data': [Array<Record<string, TBasicTableCellType>>];
    'update:filters': [Record<string, IHeadingFilters>];
    'action': [type: TBasicTableActionId, row: Record<string, TBasicTableCellType>];
}>();

const TABLE_UNITS_OPTIONS: Array<IIdName> = [
    { name: 'Inherit', id: 'inherit', info: 'Table will default to the site-wide setting (see Settings panel)' },
    { name: 'Show on hover', id: 'hover' },
    { name: 'In cell', id: 'cell' },
    { name: 'In header', id: 'header' },
    { name: 'Hidden', id: 'hidden' },
];

const YES_NO_OPTIONS: Array<IFlagOption> = [
    { title: 'Yes', value: true },
    { title: 'No', value: false },
];

const EXCLUDE_SELECTION_OPTIONS: Array<IFlagOption> = [
    { title: 'Include selection', value: false },
    { title: 'Exclude selection', value: true },
];

const SORT_DIRECTION_OPTIONS: Array<IFlagOption> = [
    { title: 'Ascending', value: true },
    { title: 'Descending', value: false },
];

const INITIAL_ROWS = 100;
const slots: Slots = useSlots();
const userSettingsStore = useUserSettingsStore();
const uiStore = useUIStore();
const { isMobileView } = storeToRefs(uiStore);

const editTableOverlay = ref<InstanceType<typeof Popover> | null>(null);
const downloadTableOverlay = ref<InstanceType<typeof Popover> | null>(null);
const numberOfScrollDowns = ref<number>(0);

const tableHeaders = ref<Array<IBasicTableHeader>>([]);
const headerFilter = ref<InstanceType<typeof Popover> | null>(null);
const rowActionsPopover = ref<InstanceType<typeof Popover> | null>(null);
const activeActionRow = ref<Record<string, TBasicTableCellType> | null>(null);
const selectedHeaderToFilter = ref<string | null>(null);
const tableUnitsSetting = ref<string>('inherit');

const currentFilters = ref<Record<string, IHeadingFilters>>({});
const sortDirectionAscending = ref<boolean>(false);
const sortHeader = ref<string | null>(null);
const columnsToHide = ref<Array<string>>([]);

const hasSlotHeader = computed(() => !!slots.header);
const displayedHeaders = computed(() => tableHeaders.value.filter(h => !columnsToHide.value.includes(h.value)));
const numberOfShownRows = computed<number>(() => Math.min(props.data.length, INITIAL_ROWS + 50 * numberOfScrollDowns.value));
const chosenTableUnitsSettings = computed<string>(() => (tableUnitsSetting.value === 'inherit' ? userSettingsStore.userSettings.otherSettings.data.tableUnitValue : tableUnitsSetting.value));

const tableHasUnits = computed(() => (props.headers.some(h => h.prefix || h.suffix)));

const enabledActions = computed<Array<IActionDescriptor>>(() => {
    if (!props.actions?.length) return [];
    return ACTION_CONFIG.filter(act => props.actions!.includes(act.id));
});

const showMobileActionsMenu = computed<boolean>(() => (
    !!props.hoverActions && isMobileView.value && !!props.actions?.length
));

function getTextFilterOptionValuesForCell (cell: TBasicTableCellType): Array<string> {
    if (cell == null) return [];
    if (isIconCell(cell)) return [];
    if (Array.isArray(cell)) {
        if (cell.length > 0 && cell.every((x): x is string => typeof x === 'string')) {
            return cell.map((item) => getPlainTextFromHtmlString(item));
        }
        if (cell.length > 0 && cell.every((x): x is number => typeof x === 'number')) {
            return cell.map((n) => String(n));
        }
        return [];
    }
    if (typeof cell === 'string') {
        return [getPlainTextFromHtmlString(cell)];
    }
    if (typeof cell === 'number') {
        return [String(cell)];
    }
    return [];
}

const filterOptions = computed(() => {
    const options = selectedHeaderToFilter.value
        ? [...new Set(props.data.flatMap((row) => getTextFilterOptionValuesForCell(row[selectedHeaderToFilter.value!])))]
        : [];
    return props.filterOptionsUnsorted ? options : options.sort();
});

const filteredTableData = computed<Array<Record<string, TBasicTableCellType>>>(() => {
    const filteredData = props.data.filter(row => Object.keys(currentFilters.value).every(filterKey => {
        if (currentFilters.value[filterKey].noFilter) return true;

        if (currentFilters.value[filterKey].type === 'numeric') {
            const cell = row[filterKey];
            const isTrend = isTrendCell(cell);
            const cellValue = isTrend ? cell.value : cell;

            if (!(typeof cellValue === 'number' || (Array.isArray(cellValue) && cellValue.every(x => typeof x === 'number')))) return false;

            const min = currentFilters.value[filterKey].options.min ?? -Infinity;
            const max = currentFilters.value[filterKey].options.max ?? Infinity;

            if (typeof cellValue === 'number') {
                const valueToCheck = currentFilters.value[filterKey].options.absolute ? Math.abs(cellValue) : cellValue;
                return valueToCheck >= min && valueToCheck <= max;
            }
            const numericValues = currentFilters.value[filterKey].options.absolute ? cellValue.map(x => Math.abs(x)) : cellValue;
            return numericValues.some(v => v >= min && v <= max);
        }
        if (currentFilters.value[filterKey].type === 'text') {
            if (!(typeof row[filterKey] === 'string' || (Array.isArray(row[filterKey]) && row[filterKey].every(x => typeof x === 'string')))) return false;

            const selectedValues = currentFilters.value[filterKey].options.values;

            if (typeof row[filterKey] === 'string') {
                const cellPlain = getPlainTextFromHtmlString(row[filterKey]);
                const match = selectedValues.some((v) => getPlainTextFromHtmlString(String(v ?? '')) === cellPlain);
                return currentFilters.value[filterKey].options.exclude ? !match : match;
            }
            const cellVal = row[filterKey] as Array<string>;
            const arrayMatch = selectedValues.some((v) => {
                const vPlain = getPlainTextFromHtmlString(String(v ?? ''));
                return cellVal.some((item) => getPlainTextFromHtmlString(item) === vPlain);
            });
            return currentFilters.value[filterKey].options.exclude ? !arrayMatch : arrayMatch;
        }
        if (currentFilters.value[filterKey].type === 'date') {
            const dateFilter = currentFilters.value[filterKey].options.date;
            if (!dateFilter || Array.isArray(row[filterKey])) return true;

            if (typeof row[filterKey] === 'string' && dayjs(row[filterKey]).isValid()) {
                const cellDate = dayjs(row[filterKey]);
                const bounds = getUnixBoundsFromTableDateFilterRange(dateFilter.dateRange);
                if (bounds) {
                    const cellUnix = cellDate.unix();
                    return cellUnix >= bounds.from && cellUnix <= bounds.to;
                }
                const dateObject = getDateObjectFromDatePicker(dateFilter);
                if (dateObject.from && cellDate.isBefore(dayjs(dateObject.from.value))) return false;
                if (dateObject.to && cellDate.isAfter(dayjs(dateObject.to.value))) return false;
                return true;
            }
        }
        return true;
    }));

    if (!sortHeader.value) return filteredData;

    const dataToSort = filteredData;

    if (dataToSort.some(row => row.disabledActions)) return dataToSort;

    if (dataToSort.some(row => Array.isArray(row[sortHeader.value!]))) {
        dataToSort.forEach(row => {
            const cell = row[sortHeader.value!];
            if (Array.isArray(cell)) {
                cell.sort((a, b) => tableValueSort(a, b));
            }
        });
    }

    return dataToSort.toSorted((aRow, bRow) => {
        let a = aRow[sortHeader.value!];
        let b = bRow[sortHeader.value!];

        if (Array.isArray(a)) a = a[0];
        if (Array.isArray(b)) b = b[0];

        if (isActionCell(a)) a = null;
        if (isActionCell(b)) b = null;

        return tableValueSort(a as TBasicTableSortValue, b as TBasicTableSortValue);
    });
});

const visibleTableRows = computed<Array<Record<string, TBasicTableCellType>>>(() => (
    filteredTableData.value.slice(0, numberOfShownRows.value)
));

function isActionDisabled (
    row: Record<string, TBasicTableCellType>,
    action: IActionDescriptor,
): boolean {
    const disabledActions = row.disabledActions as Partial<Record<TBasicTableActionId, string | boolean>> | undefined;
    const value = disabledActions?.[action.id];
    if (value == null || value === false) return false;
    return true;
}

function getDisabledReason (
    row: Record<string, TBasicTableCellType>,
    action: IActionDescriptor,
): string {
    const disabledActions = row.disabledActions as Partial<Record<TBasicTableActionId, string | boolean>> | undefined;
    const value = disabledActions?.[action.id];
    if (value == null) return '';
    return typeof value === 'string' ? value : '';
}

function isIconCell (cell: TBasicTableCellType): cell is TIconCell {
    return typeof cell === 'object' && cell !== null && 'icon' in cell;
}

function isTrendCell (cell: TBasicTableCellType): cell is ITrend {
    return typeof cell === 'object' && cell !== null && 'trend' in cell;
}

function isActionCell (cell: TBasicTableCellType): cell is Partial<Record<TBasicTableActionId, string | boolean>> {
    if (typeof cell !== 'object' || cell === null || Array.isArray(cell)) return false;
    if ('icon' in cell || 'trend' in cell) return false;
    const actionIds: TBasicTableActionId[] = ['edit', 'delete', 'view'];
    return Object.keys(cell).every(k => actionIds.includes(k as TBasicTableActionId));
}

function tableValueSort (a: TBasicTableSortValue, b: TBasicTableSortValue): number {
    if ((!a && !b)) return 0;
    if (!a) return 1;
    if (!b) return -1;

    const aIsIcon = isIconCell(a);
    const bIsIcon = isIconCell(b);

    if (aIsIcon && !bIsIcon) return 1;
    if (!aIsIcon && bIsIcon) return -1;
    if (aIsIcon && bIsIcon) return 0;

    const aIsTrend = isTrendCell(a);
    const bIsTrend = isTrendCell(b);
    if (aIsTrend && bIsTrend && a.value != null && b.value != null) {
        return sortDirectionAscending.value ? a.value - b.value : b.value - a.value;
    }

    if (typeof a === 'number' && typeof b === 'number') {
        return sortDirectionAscending.value ? a - b : b - a;
    }

    // Add other date formats here as they come up
    if ((typeof a === 'string' || typeof a === 'number') && (typeof b === 'string' || typeof b === 'number')) {
        const dateA = dayjs(a, 'D/M/YYYY');
        const dateB = dayjs(b, 'D/M/YYYY');

        if (dateA.isValid() && dateB.isValid()) {
            return sortDirectionAscending.value ? dateA.diff(dateB) : dateB.diff(dateA);
        }
    }

    if (typeof a === 'string' && typeof b === 'string') {
        return sortDirectionAscending.value ? a.localeCompare(b) : b.localeCompare(a);
    }
    return 0;
}

function emitAction (actionType: TBasicTableActionId, row: Record<string, TBasicTableCellType>): void {
    emit('action', actionType, row);
}

function openRowActionsMenu (event: Event, row: Record<string, TBasicTableCellType>): void {
    activeActionRow.value = row;
    rowActionsPopover.value?.toggle(event);
}

function handleMobileRowAction (actionType: TBasicTableActionId): void {
    if (!activeActionRow.value) return;
    emitAction(actionType, activeActionRow.value);
    rowActionsPopover.value?.hide();
}

function scrollTable (event: Event): void {
    const scrollTarget = event.target as Element;
    if (!scrollTarget || scrollTarget.scrollTop + scrollTarget.clientHeight + 10 < scrollTarget?.scrollHeight) return;
    numberOfScrollDowns.value += 1;
}

function getHeaderTitle (header: IBasicTableHeader): string {
    if (chosenTableUnitsSettings.value === 'header') {
        const prefix = header.prefix ?? '';
        const suffix = header.suffix ?? '';
        const prefixSuffixAdder = prefix === '' && suffix === '' ? '' : ` (${prefix}${suffix})`;
        return `${header.value}${prefixSuffixAdder}`;
    }
    return header.value;
}

function formatTrendCell (value: ITrend): string {
    if (value.value === null || value.value === undefined) return '-';
    const formattedValue = `${value.prefix ? value.prefix : ''}${value.value.toFixed(0).toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}${value.suffix ? ' '.concat(value.suffix) : ''}`;
    if (!value.trend) {
        return formattedValue;
    }
    const isUp = value.trend > 0;
    const trendColour = isUp ? 'var(--successGreen)' : 'var(--dangerRed)';

    const denom = Number(value.value.toFixed(0)) - Number(value.trend.toFixed(0));
    const percent = denom !== 0 ? (Number(value.trend.toFixed(0)) / denom) * 100 : 100;
    const formattedPercentage = `(${isUp ? '+' : ''}${percent.toFixed(2)}%)`;

    return `<span style="margin-right: var(--defaultSpacing)">${formattedValue}</span>

        <span style="color: ${trendColour}; font-size: var(--extraSmallFontSize)">
                ${formattedPercentage}
            </span>
        `;
}

function formatCell (value: TBasicTableCellType, header: IBasicTableHeader): string {
    if (isTrendCell(value)) {
        return formatTrendCell(value);
    }
    if (isIconCell(value)) {
        return `<i
            class="fa-solid fa-${value.icon}"
            style="color: ${value.colour || 'inherit'}; font-size: var(--smallFontSize);"
            />`;
    }
    if (header.type === 'text') {
        if (!(typeof value === 'string' || (Array.isArray(value) && value.every(x => typeof x === 'string')))) return '-';
        if (Array.isArray(value)) {
            return props.escapeTextCells
                ? value.map(entry => escapeHtml(entry)).join('<br>')
                : value.join('<br>');
        }
        return props.escapeTextCells ? escapeHtml(value) : value;
    }
    if (header.type === 'number') {
        if (!(typeof value === 'number' || (Array.isArray(value) && value.every(x => typeof x === 'number')))) return '-';
        if (Array.isArray(value)) return value.map(x => formatNumberCellValue(x, header.prefix, header.suffix)).join(', ');
        return formatNumberCellValue(value, header.prefix, header.suffix);
    }
    // should unify this
    if (header.type === 'date') {
        if (!value || !dayjs(value?.toString()).isValid()) return '-';
        if (header.dateFormat) {
            return dayjs(value.toString()).format(header.dateFormat);
        }
        if (dayjs(value.toString()).isValid()) {
            return dayjs(value.toString()).format('D/M/YYYY, HH:mm:ss');
        }
        if (dayjs(value.toString(), 'DD-MM-YYYY').isValid()) {
            return dayjs(value.toString(), 'DD-MM-YYYY').format('D/M/YYYY');
        }
    }
    return '-';
}

function formatNumberCellValue (value: number, prefix?: string, suffix?: string): string {
    const roundedNumber = Math.round((value + Number.EPSILON) * 1000) / 1000;
    const formattedNumber = roundedNumber.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    if (chosenTableUnitsSettings.value === 'cell') return `${prefix ?? ''}${formattedNumber}${suffix ?? ''}`;
    return formattedNumber;
}

function openHeaderFilter (event: Event, header: string): void {
    if (!headerFilter.value) return;

    selectedHeaderToFilter.value = header;
    headerFilter.value.toggle(event);
}

function selectRow (value: Record<string, TBasicTableCellType>): void {
    emit('select:row', value);
}

function selectCell (value: TBasicTableCellType, header: string): void {
    emit('select:cell', value, header);
}

function updateColumnOrder (event: OrderListReorderEvent): void {
    tableHeaders.value = event.value;
}

function toggleColumn (columnName: string): void {
    if (columnsToHide.value.includes(columnName)) {
        columnsToHide.value.splice(columnsToHide.value.indexOf(columnName), 1);
        return;
    }
    columnsToHide.value.push(columnName);
}

function resetHeaders (): void {
    selectedHeaderToFilter.value = null;
    sortHeader.value = null;
    sortDirectionAscending.value = false;
    setDefaultFilters();
}

function resetTable (): void {
    tableHeaders.value = props.headers;
    columnsToHide.value = [];
}

function confirmResetHeaders (): void {
    confirm.require({
        message: 'Reset all table filters?',
        accept: () => {
            resetHeaders();
        },
    });
}

function confirmResetTable (): void {
    confirm.require({
        message: 'Reset all table columns?',
        accept: () => {
            resetTable();
        },
    });
}

function openEditTableOverlay (event: Event): void {
    editTableOverlay.value?.toggle(event);
}

function openDownloadTableOverlay (event: Event): void {
    downloadTableOverlay.value?.toggle(event);
}

defineExpose({
    openDownloadTableOverlay,
    openEditTableOverlay,
});

function formatDataToJson (): Array<Array<string>> {
    const exportData = [];
    exportData.push(displayedHeaders.value.map(x => x.value));
    exportData.push([]);

    props.data.forEach(row => {
        exportData.push(displayedHeaders.value.map(h => {
            const cellVal = row[h.value];
            if (Array.isArray(cellVal)) {
                return cellVal.join(', ');
            }
            return cellVal;
        }));
    });
    return exportData;
}

// Header Filters

function updateNumericRange (selectedHeader: string, updatedValues: IMinMax): void {
    currentFilters.value[selectedHeader] = {
        options: {
            absolute: (currentFilters.value[selectedHeader].options as INumberValuesToInclude).absolute,
            min: updatedValues.min,
            max: updatedValues.max,
        },
        noFilter: updatedValues.min === null && updatedValues.max === null,
        type: 'numeric',
    };
    emit('update:filters', currentFilters.value);
}

function updateFilterValues (selectedHeader: string, updatedValues: Array<string | null>): void {
    currentFilters.value[selectedHeader] = {
        options: {
            exclude: (currentFilters.value[selectedHeader].options as ITextValuesToInclude).exclude,
            values: updatedValues,
        },
        noFilter: !updatedValues.length,
        type: 'text',
    };
    emit('update:filters', currentFilters.value);
}

function updateUseAbsoluteValues (selectedHeader: string, updatedValue: boolean): void {
    (currentFilters.value[selectedHeader].options as INumberValuesToInclude).absolute = updatedValue;
    emit('update:filters', currentFilters.value);
}

function updateExcludeSelection (selectedHeader: string, updatedValue: boolean): void {
    (currentFilters.value[selectedHeader].options as ITextValuesToInclude).exclude = updatedValue;
    emit('update:filters', currentFilters.value);
}

function updateSortDirection (selectedHeader: string, updatedValue: boolean): void {
    sortDirectionAscending.value = updatedValue;
    sortHeader.value = selectedHeader;
}

function initialiseSortDirection (): void {
    if (props.initialSort && props.initialSort.header) {
        sortHeader.value = props.initialSort.header;
        sortDirectionAscending.value = props.initialSort.ascending;
        return;
    }
    if (props.sortDefaultAlphabetical && props.headers.length) {
        const firstSortableHeader = props.headers.find(h => !h.noFilter);
        if (firstSortableHeader) {
            sortHeader.value = firstSortableHeader.value;
            sortDirectionAscending.value = true;
        }
    }
}

function updateDateFilter (selectedHeader: string, updatedValue: IAdvancedDatePickerModel | null): void {
    currentFilters.value[selectedHeader] = {
        type: 'date',
        options: {
            date: updatedValue,
        },
        noFilter: JSON.stringify(updatedValue) === JSON.stringify(getInitialDateFromHeaderString(selectedHeader)) || !updatedValue,
    };
    headerFilter.value?.hide();
    emit('update:filters', currentFilters.value);
}

function resetDateFilter (selectedHeader: string): void {
    (currentFilters.value[selectedHeader].options as IDateValuesToInclude).date = getInitialDateFromHeaderString(selectedHeader);
    headerFilter.value?.hide();
    emit('update:filters', currentFilters.value);
}

function getInitialDateFromHeaderString (headerString: string): IAdvancedDatePickerModel | null {
    const header = props.headers.find(h => h.value === headerString);
    if (!header || !header.initialFilter) return null;
    return {
        dateRange: {
            from: header.initialFilter.from.toISOString() || new Date().toISOString(),
            to: header.initialFilter.to.toISOString() || new Date().toISOString(),
        },
        dateType: 'fixed',
    };
}

function setDefaultFilters (): void {
    tableHeaders.value.filter(header => !header.noFilter).forEach(header => {
        if (header.type === 'number' || header.type === 'trend') {
            currentFilters.value[header.value] = {
                type: 'numeric',
                options: {
                    absolute: false,
                    min: null,
                    max: null,
                },
                noFilter: true,
            };
            return;
        }
        if (header.type === 'text') {
            currentFilters.value[header.value] = {
                type: 'text',
                options: {
                    values: [],
                    exclude: false,
                },
                noFilter: true,
            };
            return;
        }
        if (header.type === 'date') {
            currentFilters.value[header.value] = {
                type: 'date',
                options: {
                    date: getInitialDateFromHeaderString(header.value),
                },
                noFilter: !getInitialDateFromHeaderString(header.value),
            };
        }
    });
}

// Lifecycle

function initialise (): void {
    resetTable();
    resetHeaders();
    initialiseSortDirection();
}

watch(() => props.data, () => {
    if (!props.externalFiltering) {
        initialise();
    }
});

watch(filteredTableData, () => {
    emit('update:filtered-data', filteredTableData.value);
});

initialise();
</script>

<style lang="scss" scoped>
.basic-table-wrapper {
    max-height: 100%;
    display: grid;
    grid-auto-rows: auto 1fr;
    overflow-y: auto;
}

.table-wrapper {
    height: 100%;
    overflow: auto;

    table {
        position: relative;
    }

    th {
        text-align: left;

        .header-text {
            font-size: var(--defaultFontSize);
        }
        .header-button {
            padding-left: 0;
        }
        &.hover-actions-spacer {
            width: 5rem;
        }
    }

    tbody tr {
        cursor: pointer;

        &.has-actions {
            position: relative;
        }

        &.cell-selection {
            cursor: default;

            &:hover td {
                background-color: var(--neutral100);
            }

            td.clickable:hover {
                background-color: var(--neutral300);
                cursor: pointer;
            }
        }
    }

    &.hover-actions {
        .row-actions-overlay-cell {
            width: 0;
            max-width: 0;
            padding: 0;
            vertical-align: middle;
            overflow: visible;
            position: relative;
            border: none;
        }

        .row-actions-overlay {
            position: absolute;
            right: 0;
            top: 0;
            bottom: 0;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            pointer-events: none;
            padding-right: var(--defaultSpacing);
        }

        .action-button {
            opacity: 0;
            transform: translateX(0);
            transition:
                opacity 0.3s ease,
                transform 0.3s ease;
            transition-delay: calc(var(--action-delay-leave, 0) * 1ms);
        }

        tbody tr:hover {
            .row-actions-overlay {
                pointer-events: auto;
            }

            .action-button {
                opacity: 1;
                transform: translateX(calc(var(--defaultSpacing) * -1));
                transition-delay: calc(var(--action-delay, 0) * 1ms + 200ms);
            }
        }

        &.mobile-actions-menu {
            @include responsive(sm) {
                th.hover-actions-spacer,
                th.row-actions-leading-cell {
                    width: 2.5rem;
                }

                .row-actions-overlay-cell {
                    width: auto;
                    max-width: none;
                    padding: var(--extraSmallSpacing) var(--smallSpacing);
                }

                .row-actions-leading-cell {
                    position: sticky;
                    left: 0;
                    z-index: 1;
                    background-color: var(--neutral0);
                    box-shadow: 2px 0 4px -2px rgba(0, 0, 0, 0.12);
                }

                thead .row-actions-leading-cell {
                    z-index: 3;
                    background-color: var(--neutral100);
                }

                tbody tr:hover .row-actions-leading-cell {
                    background-color: var(--neutral100);
                }

                tbody tr.cell-selection:hover .row-actions-leading-cell {
                    background-color: var(--neutral0);
                }
            }
        }
    }

    .row-actions-trigger {
        opacity: 1;
        padding: var(--extraSmallSpacing);
    }

    .row-actions-menu {
        display: flex;
        flex-direction: column;
        gap: var(--smallSpacing);
        min-width: 10rem;
    }

    .row-actions-static-cell {
        .row-actions-overlay {
            display: flex;
            align-items: center;
            gap: var(--defaultSpacing);
            transition: opacity 0.3s ease-in-out;
            opacity: 0.4;

            .action-button {
                padding: var(--extraSmallSpacing) var(--smallSpacing);

                &:first-child {
                    padding: var(--extraSmallSpacing) var(--smallSpacing) var(--extraSmallSpacing) 0;
                }
            }
        }
    }

    tbody tr:hover {
        .row-actions-static-cell {
            .row-actions-overlay {
                opacity: 1;
            }
        }
    }

    .action-button-static {
        flex-shrink: 0;
    }
}

.header-row {
    align-items: center;
    display: flex;
    justify-content: space-between;
    padding: var(--smallSpacing);
    gap: var(--defaultSpacing);

    &.reduced-header {
        padding: 0;

        .button-row {
            height: 0;
        }
    }
}

.header-filter {
    display: flex;
    flex-direction: column;
    gap: var(--largeSpacing);
    padding: var(--defaultSpacing);
    background-color: var(--neutral0);
    border-radius: var(--borderRadiusOuter);

    .header-filter-section {
        display: flex;
        flex-direction: column;
        gap: var(--defaultSpacing);
    }

    .checkbox-wrapper {
        display: flex;
        align-items: center;
        gap: var(--defaultSpacing);
    }
}

.edit-table-wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--largeSpacing);
    padding: var(--defaultSpacing);
    background-color: var(--neutral0);
    border-radius: var(--borderRadiusOuter);

    .edit-table-header {
        display: flex;
        align-items: center;
        justify-content: space-between;

        h4 {
            margin: 0;
        }
    }

    .list-item {
        display: flex;
        gap: var(--defaultSpacing);
        justify-content: space-between;
        user-select: none;
        width: 100%;
    }

    .edit-table-units {
        display: flex;
        flex-direction: column;
        align-items: left;
        gap: var(--smallSpacing);
    }

    .edit-table-resets {
        display: flex;
        flex-direction: column;
        gap: var(--defaultSpacing);

        .button-holder {
            display: flex;
            gap: var(--defaultSpacing);
        }
    }
}
</style>
